import { useQuery } from '@tanstack/react-query';
import { getSession, type SessionInfo } from '../api/auth.api';
import type { EffectiveEngageRole, EngageRole } from '../api/org-users.api';
import { useAuthStore } from '../stores/auth.store';

/**
 * Engage role ranking. UNALLOCATED (synced from JT, no role given yet) ranks
 * the same as VIEW.
 */
export const ENGAGE_ROLE_RANK: Record<EffectiveEngageRole, number> = {
  ADMIN: 4,
  PUBLISH: 3,
  WRITE: 2,
  VIEW: 1,
  UNALLOCATED: 1,
};

export function engageRoleRank(role: EffectiveEngageRole | null | undefined): number {
  return (role && ENGAGE_ROLE_RANK[role]) || 1;
}

/** True when `role` is at least `min` (e.g. ADMIN satisfies PUBLISH). */
export function hasEngageRole(role: EffectiveEngageRole | null | undefined, min: EngageRole): boolean {
  return engageRoleRank(role) >= ENGAGE_ROLE_RANK[min];
}

/**
 * The session as the server sees it right now: effective role (reflects role
 * changes within ~30s of an admin changing them), JT access, and impersonation
 * details. UI affordances only — every API route enforces its own role.
 *
 * The token is part of the query key so a different sign-in in the same tab
 * never reuses a cached role; invalidating ['auth', 'session'] still matches.
 */
export function useSession() {
  const token = useAuthStore((s) => s.token);
  return useQuery<SessionInfo>({
    queryKey: ['auth', 'session', token],
    queryFn: getSession,
    enabled: !!token,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The effective Engage role from the live session, or `undefined` while the
 * session hasn't loaded yet (or couldn't be loaded). Callers that need a
 * yes/no answer should use useCan(), which handles the unknown state.
 */
export function useEngageRole(): EffectiveEngageRole | undefined {
  const { data } = useSession();
  return data?.engage_role;
}

/**
 * Whether the current user's Engage role is at least `min`. UX-only hiding:
 * the server enforces every action regardless of what this returns.
 *
 * Behaviour before the session has loaded (or if /auth/session failed):
 *  - min = 'ADMIN'  → falls back to the pre-role heuristic (JT admin/owner, or
 *    super-admin), so company settings don't flash in for regular users and
 *    don't flash out for JT admins.
 *  - anything lower → true. Buttons stay visible until we know the role, so
 *    people who can use them never see them flash out; low-role users may see
 *    them for a moment before they're hidden, and the server refuses the action
 *    if they're clicked.
 * Once the session loads, the server's role always wins.
 */
export function useCan(min: EngageRole): boolean {
  const role = useEngageRole();
  const legacyAdmin = useAuthStore(
    (s) => s.isSuperAdmin || ['admin', 'owner'].includes((s.user?.role ?? '').toLowerCase()),
  );
  if (role) return hasEngageRole(role, min);
  return min === 'ADMIN' ? legacyAdmin : true;
}
